import React, { useState, useRef } from "react";
import { post, patch, destroy } from "@rails/request.js";
import { motion } from "framer-motion";
import {
  FiAlignLeft, FiAlignJustify, FiList, FiCheckSquare, FiTrash2,
  FiMove, FiBarChart2, FiHash, FiImage,
} from "react-icons/fi";

import { Toasts, useToasts } from "./../shared/Toast";
import EditableName from "./../shared/EditableName";
import ViewControls from "./../shared/ViewControls";
import FullscreenPreview from "./../shared/FullscreenPreview";
import SurveyPreview from "./Preview";
import { Modal, useModal } from "./../shared/Modal";
import { NpsStrip, NumericStrip } from "./screens";

// Survey studio — same shell as capform/widget. Chromeless studio
// header up top (back link + "editing —" + EditableName + ViewControls
// + share). Accordion sidebar (1/3) + live preview pane (2/3) below.
//
// Sidebar sections, in order:
//   welcome   blue    — toggle + title + text
//   questions orange  — draggable list + "+ add" grid + inline editor
//                       for the SELECTED question (prompt, options, etc)
//   thanks    pink    — toggle + title + text + redirect URL
//   testim.   green   — toggle + prompt
//   design    purple  — background style + colors
//   settings  red     — anonymous toggle, default labels, approval

// ─── Kind config ─────────────────────────────────────────────────────
const KINDS = [
  { key: "short",   label: "Short answer",    Icon: FiAlignLeft,    helper: "single-line text" },
  { key: "long",    label: "Long answer",     Icon: FiAlignJustify, helper: "multi-line text" },
  { key: "single",  label: "Single choice",   Icon: FiCheckSquare,  helper: "radio buttons or dropdown" },
  { key: "multi",   label: "Multiple choice", Icon: FiList,         helper: "checkboxes — pick any number" },
  { key: "nps",     label: "NPS (0-10)",      Icon: FiBarChart2,    helper: "would you recommend us?" },
  { key: "numeric", label: "Number (1-10)",   Icon: FiHash,         helper: "plain 1-10 picker" },
  { key: "file",    label: "Image upload",    Icon: FiImage,        helper: "single image" },
];
const KIND_BY_KEY = Object.fromEntries(KINDS.map(k => [k.key, k]));

const apiBase = (projectId, surveyId) => `/projects/${projectId}/surveys/${surveyId}`;

// Re-export so Show.jsx and any other consumer can still pull these
// strips from ./Edit if they were already wired that way (back-compat).
export { NpsStrip, NumericStrip };

const SECTIONS = [
  { id: "welcome",     label: "welcome",     color: "text-ssblue",   accent: "#3E8DFF" },
  { id: "questions",   label: "questions",   color: "text-ssorange", accent: "#FF8F47" },
  { id: "thanks",      label: "thanks",      color: "text-sspink",   accent: "#FF7AAC" },
  { id: "testimonial", label: "testimonial", color: "text-ssgreen",  accent: "#3EB567" },
  { id: "design",      label: "design",      color: "text-sspurple", accent: "#8a74b9" },
  { id: "settings",    label: "settings",    color: "text-ssred",    accent: "#EF4444" },
];

// ─── Top-level studio ────────────────────────────────────────────────

export default function SurveyEdit({ project_id, survey_id, init_survey, project_labels, project_capforms, back_url }) {
  const [survey, setSurvey]     = useState(init_survey.survey);
  const [section, setSection]   = useState(survey.questions.length ? "questions" : "welcome");
  const [selected, setSelected] = useState(survey.questions[0]?.id || null);
  const [previewMode, setPreviewMode] = useState("desktop");
  const [breakoutOpen, setBreakoutOpen] = useState(false);
  const [saveSpin, setSaveSpin] = useState(false);
  const { toasts, setToasts, addToast } = useToasts();

  const backUrl  = back_url || `/projects/${project_id}/surveys`;
  const shareUrl = `${apiBase(project_id, survey.id)}/share`;
  const publicUrl = `/sv/${survey.uuid}`;

  // ─── API helpers ───────────────────────────────────────────────────

  // Every edit here saves the moment a field blurs, with nothing on screen
  // to say so. One toast per field would be noise when you tab through a
  // panel, so coalesce: the last save in a burst wins and you get a single
  // "Saved". Failures toast immediately and are never coalesced.
  const savedTimer = useRef(null);
  const notifySaved = () => {
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => {
      savedTimer.current = null;
      addToast("success", "Saved");
    }, 600);
  };

  // Don't fire a toast into a component that's already gone.
  React.useEffect(() => () => { if (savedTimer.current) clearTimeout(savedTimer.current); }, []);

  const patchSurvey = async (attrs) => {
    setSaveSpin(true);
    const response = await patch(`/api/v1/surveys/${survey.id}`, {
      body: { survey: attrs },
      contentType: "application/json", responseKind: "json",
    });
    setSaveSpin(false);
    if (response.ok) {
      const json = await response.json;
      setSurvey(json.survey);
      notifySaved();
    } else {
      addToast("error", "Couldn't save");
    }
  };

  const addQuestion = async (kind) => {
    const response = await post(`${apiBase(project_id, survey.id)}/questions`, {
      body: { question: { kind, prompt: defaultPromptFor(kind), options: defaultOptionsFor(kind) } },
      contentType: "application/json", responseKind: "json",
    });
    if (response.ok) {
      const q = await response.json;
      setSurvey(s => ({ ...s, questions: [...s.questions, q] }));
      setSelected(q.id);
      setSection("questions");
      addToast("success", "Question added");
    } else {
      addToast("error", "Couldn't add that question");
    }
  };

  const updateQuestion = async (id, attrs) => {
    const response = await patch(`${apiBase(project_id, survey.id)}/questions/${id}`, {
      body: { question: attrs },
      contentType: "application/json", responseKind: "json",
    });
    if (response.ok) {
      const q = await response.json;
      setSurvey(s => ({ ...s, questions: s.questions.map(x => x.id === id ? q : x) }));
      notifySaved();
    } else {
      addToast("error", "Couldn't save that question");
    }
  };

  const deleteQuestion = async (id) => {
    const response = await destroy(`${apiBase(project_id, survey.id)}/questions/${id}`, { responseKind: "json" });
    if (!response.ok) {
      const body = await response.json.catch(() => null);
      addToast("error", body?.errors?.[0] || "Couldn't delete that question");
      return;
    }
    setSurvey(s => ({ ...s, questions: s.questions.filter(x => x.id !== id) }));
    if (selected === id) setSelected(null);
    addToast("success", "Question deleted");
  };

  const reorder = async (order) => {
    const previous = survey.questions;
    setSurvey(s => ({ ...s, questions: order.map(id => s.questions.find(q => q.id === id)).filter(Boolean) }));
    const response = await patch(`${apiBase(project_id, survey.id)}/questions/reorder`, {
      body: { order }, contentType: "application/json", responseKind: "json",
    });
    if (response.ok) {
      notifySaved();
    } else {
      // Put the old order back rather than leaving the screen disagreeing
      // with the database.
      setSurvey(s => ({ ...s, questions: previous }));
      addToast("error", "Couldn't reorder");
    }
  };

  // What does the preview show? Derived from the open section + the
  // selected question. "questions" → render the selected question.
  const previewPage = section === "questions" ? "question" : section;

  // ─── Render ────────────────────────────────────────────────────────

  return (
    <div className="h-full flex flex-col">
      <Toasts toasts={toasts} setToasts={setToasts} />

      <header className="flex-none border-b-[3px] border-black bg-white">
        <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2.5">
          <a
            href={backUrl}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider bg-white border-[2px] border-black text-slate-700 hover:bg-ssyellow hover:text-slate-900 shadow-[2px_2px_0_rgba(0,0,0,0.25)] hover:shadow-[3px_3px_0_rgba(0,0,0,0.35)] hover:-translate-y-0.5 transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="h-3.5 w-3.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
            <span className="hidden sm:inline">back to surveys</span>
            <span className="sm:hidden">back</span>
          </a>

          <div className="flex-1 min-w-0 flex items-center gap-2">
            <span className="hidden md:inline zz-hand text-lg text-ssgreen leading-none flex-none">editing —</span>
            <EditableName
              endpoint={`/api/v1/surveys/${survey.id}`}
              resourceKey="survey"
              name={survey.name}
              fallback="untitled survey"
              onChange={(name) => setSurvey(prev => ({ ...prev, name }))}
              onError={(msg) => addToast("error", msg)}
              onSuccess={() => addToast("success", "Name updated")}
            />
          </div>

          <div className="flex-none flex items-center gap-1.5 sm:gap-2">
            <ViewControls
              mode={previewMode}
              setMode={setPreviewMode}
              onBreakout={() => setBreakoutOpen(true)}
              breakoutActive={breakoutOpen}
            />
            {saveSpin && (
              <svg className="animate-spin h-4 w-4 text-slate-500" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            <a href={shareUrl} className="zz-btn sm green">share</a>
          </div>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="flex flex-col md:flex-row gap-4 sm:gap-5 p-3 sm:p-5">
          <aside className="w-full md:w-1/2 md:flex-none">
            <div className="bg-white border-[3px] border-black md:h-[calc(100vh-160px)] md:overflow-y-auto">
              <div className="divide-y-[3px] divide-black">
                {SECTIONS.map(s => {
                  const isOpen = section === s.id;
                  return (
                    <div key={s.id}>
                      <button
                        type="button"
                        onClick={() => setSection(isOpen ? null : s.id)}
                        aria-expanded={isOpen}
                        className={`w-full px-5 py-4 flex justify-between items-center text-left transition-colors ${isOpen ? "bg-slate-50" : "hover:bg-slate-50"}`}
                      >
                        <span className={`zz-hand text-2xl ${s.color}`}>{s.label}</span>
                        <span
                          className={`text-xl font-black transition-transform ${isOpen ? "rotate-90" : ""}`}
                          style={{ color: s.accent }}
                          aria-hidden="true"
                        >
                          ›
                        </span>
                      </button>
                      <motion.div
                        initial={false}
                        animate={{
                          height: isOpen ? "auto" : 0,
                          display: isOpen ? "block" : "none",
                          opacity: isOpen ? 1 : 0,
                        }}
                        className="relative z-20 overflow-hidden"
                      >
                        {s.id === "welcome"     && <WelcomePanel     survey={survey} onChange={patchSurvey} />}
                        {s.id === "questions"   && <QuestionsPanel
                                                     survey={survey}
                                                     selected={selected}
                                                     setSelected={setSelected}
                                                     addQuestion={addQuestion}
                                                     updateQuestion={updateQuestion}
                                                     deleteQuestion={deleteQuestion}
                                                     reorder={reorder}
                                                  />}
                        {s.id === "thanks"      && <ThanksPanel      survey={survey} onChange={patchSurvey} />}
                        {s.id === "testimonial" && <TestimonialPanel survey={survey}
                                                                    projectId={project_id}
                                                                    capforms={project_capforms}
                                                                    onChange={patchSurvey} />}
                        {s.id === "design"      && <DesignPanel      survey={survey} onChange={patchSurvey} />}
                        {s.id === "settings"    && <SettingsPanel    survey={survey} projectLabels={project_labels} onChange={patchSurvey} />}
                      </motion.div>
                    </div>
                  );
                })}
              </div>
            </div>
          </aside>

          <section className="w-full md:flex-1 min-w-0">
            <SurveyPreview
              survey={survey}
              page={previewPage}
              previewMode={previewMode}
              selectedId={selected}
            />
          </section>
        </div>
      </div>

      {breakoutOpen && (
        <FullscreenPreview url={publicUrl} title="Survey fullscreen preview" onClose={() => setBreakoutOpen(false)} />
      )}
    </div>
  );
}

// ─── Sidebar panels ──────────────────────────────────────────────────

function WelcomePanel({ survey, onChange }) {
  return (
    <div className="flex flex-col gap-y-4 p-4">
      <Toggle
        label="Show welcome screen"
        checked={survey.welcome_step}
        onChange={(v) => onChange({ welcome_step: v })}
      />
      {survey.welcome_step && (
        <>
          <div>
            <label className="zz-label">Welcome title</label>
            <input
              type="text"
              defaultValue={survey.welcome_title || ""}
              onBlur={(e) => onChange({ welcome_title: e.target.value })}
              className="zz-input"
              placeholder={survey.name}
            />
          </div>
          <div>
            <label className="zz-label">Welcome text</label>
            <textarea
              rows={3}
              defaultValue={survey.welcome_text || ""}
              onBlur={(e) => onChange({ welcome_text: e.target.value })}
              className="zz-input"
              placeholder="A short intro paragraph."
            />
          </div>
        </>
      )}
    </div>
  );
}

function ThanksPanel({ survey, onChange }) {
  return (
    <div className="flex flex-col gap-y-4 p-4">
      <Toggle
        label="Show thanks screen"
        checked={survey.thanks_step}
        onChange={(v) => onChange({ thanks_step: v })}
      />
      {survey.thanks_step && (
        <>
          <div>
            <label className="zz-label">Thanks title</label>
            <input type="text" defaultValue={survey.thanks_title || ""} onBlur={(e) => onChange({ thanks_title: e.target.value })} className="zz-input" placeholder="Thanks!" />
          </div>
          <div>
            <label className="zz-label">Thanks text</label>
            <textarea rows={3} defaultValue={survey.thanks_text || ""} onBlur={(e) => onChange({ thanks_text: e.target.value })} className="zz-input" placeholder="A short closing message." />
          </div>
          <div>
            <label className="zz-label">Redirect URL (optional)</label>
            <input type="url" defaultValue={survey.thanks_url || ""} onBlur={(e) => onChange({ thanks_url: e.target.value })} className="zz-input" placeholder="https://yoursite.com/thank-you" />
            <p className="text-[11px] text-slate-500 mt-1">If set, the respondent is redirected here instead of seeing the thanks screen.</p>
          </div>
        </>
      )}
    </div>
  );
}

// The testimonial step hands the capture itself to a capform: that form
// supplies the prompt copy, decides whether the resulting proof is
// approved on submit, and lends it the form's labels. Leaving the picker
// on "Project default" follows whichever form the project defaults to,
// so a survey doesn't go stale when that changes.
function TestimonialPanel({ survey, projectId, capforms, onChange }) {
  const forms = capforms || [];
  const defaultForm = forms.find(f => f.is_default);
  const activeId = survey.capform_id || defaultForm?.id || null;

  return (
    <div className="flex flex-col gap-y-4 p-4">
      <Toggle
        label="Ask for a testimonial at the end"
        checked={survey.testimonial_step}
        onChange={(v) => onChange({ testimonial_step: v })}
      />
      {survey.testimonial_step && (
        <>
          <div>
            <label className="zz-label">Testimonial prompt</label>
            <input type="text" defaultValue={survey.testimonial_prompt || ""} onBlur={(e) => onChange({ testimonial_prompt: e.target.value })} className="zz-input" placeholder="Would you mind leaving a testimonial?" />
          </div>

          <div>
            <label className="zz-label">Capture form</label>
            <select
              value={survey.capform_id || ""}
              onChange={(e) => onChange({ capform_id: e.target.value === "" ? null : Number(e.target.value) })}
              className="zz-input"
            >
              <option value="">
                {defaultForm ? `Project default (${defaultForm.name})` : "Project default (one gets created)"}
              </option>
              {forms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              Supplies the prompt shown above the recorder, decides approval, and
              lends its labels to whatever gets captured.
              {activeId && (
                <> <a href={`/projects/${projectId}/capforms/${activeId}/edit`} className="zz-link" data-turbo="false">Edit that form</a>.</>
              )}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Design panel ────────────────────────────────────────────────────

const BACKGROUND_STYLES = [ "wavey", "bubbles", "speech", "stars", "retro", "confetti", "gradient", "dots", "doodles", "solid" ];

function DesignPanel({ survey, onChange }) {
  const data = survey.data || {};
  const setData = (patch) => onChange({ data: { ...data, ...patch } });
  return (
    <div className="flex flex-col gap-y-4 p-4">
      <div>
        <p className="zz-label mb-2">Background pattern</p>
        <div className="flex flex-wrap gap-1">
          {BACKGROUND_STYLES.map(b => {
            const on = (data.background_style || "wavey") === b;
            return (
              <button key={b} type="button" onClick={() => setData({ background_style: b })}
                      className={`text-[11px] font-bold px-2 py-1 rounded border-[2px] ${on ? "bg-slate-900 text-white border-black" : "bg-white border-black/30 hover:border-black"}`}>
                {b}
              </button>
            );
          })}
        </div>
      </div>
      <ColorRow label="Background color" value={data.background_color || "#3E8DFF"} onChange={(v) => setData({ background_color: v })} />
      <ColorRow label="Primary color"    value={data.primary_color    || "#FF7AAC"} onChange={(v) => setData({ primary_color: v })} />
      <p className="text-[11px] text-slate-500">Retro and gradient styles keep their curated palettes.</p>
    </div>
  );
}

function ColorRow({ label, value, onChange }) {
  return (
    <div>
      <p className="zz-label mb-2">{label}</p>
      <div className="flex items-center gap-3 flex-wrap">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)}
               className="h-12 w-16 border-[3px] border-black cursor-pointer bg-white" />
        <span className="text-xs font-mono font-bold text-slate-600">{value.toUpperCase()}</span>
      </div>
    </div>
  );
}

// ─── Settings panel ──────────────────────────────────────────────────

function SettingsPanel({ survey, projectLabels, onChange }) {
  return (
    <div className="flex flex-col gap-y-5 p-4">
      <Toggle
        label="Anonymous mode"
        hint="Skip the contact step. If a testimonial is left, only a name is collected."
        checked={survey.anonymous}
        onChange={(v) => onChange({ anonymous: v })}
      />

      <div>
        <p className="zz-label mb-2">Approval (when a testimonial is captured)</p>
        <p className="text-xs text-slate-500">
          Set by the capture form the testimonial step uses. Pick it under
          the testimonial section.
        </p>
      </div>

      {projectLabels?.length > 0 && (
        <div>
          <p className="zz-label mb-2">Auto-apply labels</p>
          <p className="text-xs text-slate-500 mb-2">Every captured testimonial gets these labels.</p>
          <div className="flex flex-wrap gap-1">
            {projectLabels.map(l => {
              const on = (survey.default_label_ids || []).includes(l.id);
              const names = (survey.default_label_ids || []).map(id => projectLabels.find(x => x.id === id)?.name).filter(Boolean);
              const next = on ? names.filter(n => n !== l.name) : [...names, l.name];
              return (
                <button key={l.id} type="button" onClick={() => onChange({ default_label_names: next })}
                        className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border-[2px] ${on ? "bg-sspink text-white border-black" : "bg-white border-black/30 hover:border-black"}`}>
                  <span className="w-2 h-2 rounded-full inline-block" style={{ background: l.color }}></span>
                  {l.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Questions panel ─────────────────────────────────────────────────
// Draggable list + "+ add" grid + inline editor for the SELECTED question
// (prompt / options / show-if / branching logic).

function QuestionsPanel({ survey, selected, setSelected, addQuestion, updateQuestion, deleteQuestion, reorder }) {
  const selectedQuestion = survey.questions.find(q => q.id === selected);

  const { modalOpen, setModalOpen } = useModal();

  const pickKind = (key) => { setModalOpen(false); addQuestion(key); };

  return (
    <div className="flex flex-col gap-y-4 p-4">
      <div className="flex justify-end">
        <button type="button" onClick={() => setModalOpen(true)} className="zz-btn sm blue shrink-0">
          + add a question
        </button>
      </div>

      <QuestionList
        questions={survey.questions}
        selectedId={selected}
        onSelect={setSelected}
        onDelete={deleteQuestion}
        onReorder={reorder}
      />

      <QuestionKindPicker modalOpen={modalOpen} setModalOpen={setModalOpen} onPick={pickKind} />

      {selectedQuestion && (
        <div className="border-t-[2px] border-black/10 pt-4">
          <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-2">editing question</p>
          <QuestionEditor
            key={selectedQuestion.id}
            question={selectedQuestion}
            allQuestions={survey.questions}
            onChange={(attrs) => updateQuestion(selectedQuestion.id, attrs)}
          />
        </div>
      )}
    </div>
  );
}

// ─── Add-a-question picker ───────────────────────────────────────────
//
// Each kind gets a small rendering of what the respondent actually sees,
// so the choice is made by recognising the shape rather than by reading
// seven labels.

function KindSketch({ kind }) {
  const bar = "h-1.5 rounded-full bg-slate-300";
  const dot = "h-3 w-3 rounded-full border-[2px] border-slate-400";
  switch (kind) {
    case "short":
      return <div className="h-7 rounded border-[2px] border-slate-300 bg-white" />;
    case "long":
      return <div className="h-7 rounded border-[2px] border-slate-300 bg-white flex flex-col justify-center gap-1 px-1.5">
        <span className={`${bar} w-full`} /><span className={`${bar} w-2/3`} />
      </div>;
    case "single":
      return <div className="flex flex-col gap-1.5">
        {[ 0, 1 ].map(i => <span key={i} className="flex items-center gap-1.5">
          <span className={`${dot} ${i === 0 ? "border-ssblue bg-ssblue" : ""}`} /><span className={`${bar} w-12`} />
        </span>)}
      </div>;
    case "multi":
      return <div className="flex flex-col gap-1.5">
        {[ 0, 1 ].map(i => <span key={i} className="flex items-center gap-1.5">
          <span className={`h-3 w-3 rounded-[3px] border-[2px] ${i === 0 ? "border-ssblue bg-ssblue" : "border-slate-400"}`} /><span className={`${bar} w-12`} />
        </span>)}
      </div>;
    case "nps":
      return <div className="flex gap-[3px]">
        {[ ...Array(9) ].map((_, i) => (
          <span key={i} className="h-5 flex-1 rounded-[2px]"
                style={{ background: i < 3 ? "#FF7AAC" : i < 6 ? "#FFD23A" : "#3EB567" }} />
        ))}
      </div>;
    case "numeric":
      return <div className="flex gap-[3px]">
        {[ ...Array(6) ].map((_, i) => (
          <span key={i} className="h-5 flex-1 rounded-[2px] border-[2px] border-slate-300 bg-white" />
        ))}
      </div>;
    case "file":
      return <div className="h-7 rounded border-[2px] border-dashed border-slate-400 bg-white flex items-center justify-center">
        <FiImage className="text-slate-400" />
      </div>;
    default:
      return null;
  }
}

function QuestionKindPicker({ modalOpen, setModalOpen, onPick }) {
  return (
    <Modal modalOpen={modalOpen} setModalOpen={setModalOpen} maxWidth="max-w-3xl">
      <div className="p-1">
        <p className="zz-hand text-2xl text-ssblue leading-none">add a question —</p>
        <h2 className="zz-display text-2xl font-black mt-1">what are you asking?</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
          {KINDS.map(k => (
            <button key={k.key} type="button" onClick={() => onPick(k.key)}
                    className="zz-panel text-left transition-all hover:border-black hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_#111]">
              <div className="mb-3"><KindSketch kind={k.key} /></div>
              <p className="flex items-center gap-2 font-black text-sm"><k.Icon className="shrink-0 text-ssblue" /> {k.label}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">{k.helper}</p>
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}

// ─── Question list ───────────────────────────────────────────────────

function QuestionList({ questions, selectedId, onSelect, onDelete, onReorder }) {
  const dragIdRef = useRef(null);
  // Which row the pointer is over, and whether it would land above or
  // below it — drives the insertion line so the drop target is visible
  // rather than guessed at.
  const [dragId, setDragId]   = useState(null);
  const [dropAt, setDropAt]   = useState(null); // { id, edge: "top" | "bottom" }

  const clearDrag = () => { dragIdRef.current = null; setDragId(null); setDropAt(null); };

  const onDragStart = (e, id) => {
    dragIdRef.current = id;
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = (e, overId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragIdRef.current == null || overId === dragIdRef.current) { setDropAt(null); return; }
    const box  = e.currentTarget.getBoundingClientRect();
    const edge = (e.clientY - box.top) < box.height / 2 ? "top" : "bottom";
    setDropAt(prev => (prev && prev.id === overId && prev.edge === edge) ? prev : { id: overId, edge });
  };

  const onDrop = (e, dropOnId) => {
    e.preventDefault();
    const from = dragIdRef.current;
    const edge = dropAt && dropAt.id === dropOnId ? dropAt.edge : "top";
    clearDrag();
    if (from == null || from === dropOnId) return;
    const order = questions.map(q => q.id);
    const fromIdx = order.indexOf(from);
    order.splice(fromIdx, 1);
    // Recompute after the removal so the index still points at the row
    // the pointer was actually over.
    const overIdx = order.indexOf(dropOnId);
    order.splice(edge === "top" ? overIdx : overIdx + 1, 0, from);
    onReorder(order);
  };

  const onKeyDown = (e, id) => {
    if (!e.altKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
    e.preventDefault();
    const order = questions.map(q => q.id);
    const idx = order.indexOf(id);
    const target = e.key === "ArrowUp" ? idx - 1 : idx + 1;
    if (target < 0 || target >= order.length) return;
    [ order[idx], order[target] ] = [ order[target], order[idx] ];
    onReorder(order);
  };

  if (!questions.length) {
    return <p className="text-xs text-slate-500 italic">No questions yet — add one above.</p>;
  }

  return (
    <ul className="space-y-1" aria-label="questions">
      {questions.map((q, i) => {
        const Icon = KIND_BY_KEY[q.kind]?.Icon || FiAlignLeft;
        const isSelected = q.id === selectedId;
        return (
          <li key={q.id}
              tabIndex={0}
              draggable
              onDragStart={(e) => onDragStart(e, q.id)}
              onDragOver={(e) => onDragOver(e, q.id)}
              onDrop={(e) => onDrop(e, q.id)}
              onDragEnd={clearDrag}
              onKeyDown={(e) => onKeyDown(e, q.id)}
              className={`relative flex items-center gap-2 px-2 py-2 rounded border-[2px] cursor-pointer outline-none focus:ring-2 focus:ring-sspink ${isSelected ? "bg-white border-black shadow-[2px_2px_0_rgba(0,0,0,0.85)]" : "bg-white/50 border-black/20 hover:border-black"} ${dragId === q.id ? "opacity-40" : ""}`}
              onClick={() => onSelect(q.id)}
              title="drag to reorder · Alt+↑/↓ to nudge">
            {dropAt && dropAt.id === q.id && (
              <span aria-hidden="true"
                    className={`absolute left-0 right-0 h-[3px] bg-ssblue rounded-full ${dropAt.edge === "top" ? "-top-[2px]" : "-bottom-[2px]"}`} />
            )}
            <FiMove className="text-slate-400 shrink-0" />
            <Icon className="shrink-0 text-slate-600" />
            <span className="text-xs font-bold text-slate-500 shrink-0">{i + 1}.</span>
            <span className="truncate text-sm">{q.prompt || <em className="text-slate-400">untitled</em>}</span>
            <button onClick={(e) => { e.stopPropagation(); if (confirm("Delete this question? Answers already collected are kept.")) onDelete(q.id); }}
                    className="ml-auto text-slate-400 hover:text-ssred shrink-0" title="delete">
              <FiTrash2 />
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// ─── Per-question editor (inline in the questions panel) ─────────────

function QuestionEditor({ question, allQuestions, onChange }) {
  const showOptions = question.kind === "single" || question.kind === "multi";
  return (
    <div className="flex flex-col gap-y-3">
      <div>
        <label className="zz-label">Prompt</label>
        <input type="text" defaultValue={question.prompt} onBlur={(e) => onChange({ prompt: e.target.value })} className="zz-input text-base" />
      </div>
      <div>
        <label className="zz-label">Helper text (optional)</label>
        <input type="text" defaultValue={question.helper_text || ""} onBlur={(e) => onChange({ helper_text: e.target.value })} placeholder="A sub-prompt or hint" className="zz-input text-sm" />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={question.required} onChange={(e) => onChange({ required: e.target.checked })} className="h-4 w-4 accent-sspink" />
        Required
      </label>

      {showOptions && (
        <div>
          <label className="zz-label">Options</label>
          <OptionsEditor key={question.id} value={question.options || []} onChange={(opts) => onChange({ options: opts })} />
        </div>
      )}

      {question.kind === "nps" && (
        <p className="text-[11px] text-slate-500 italic">NPS options (0-10) auto-fill. Wire branches in the logic block below.</p>
      )}
      {question.kind === "numeric" && (
        <p className="text-[11px] text-slate-500 italic">Numeric options (1-10) auto-fill.</p>
      )}
      {question.kind === "file" && (
        <p className="text-[11px] text-slate-500 italic">Respondents see a single image uploader.</p>
      )}

      <ShowIfEditor question={question} allQuestions={allQuestions} onChange={onChange} />
      <LogicEditor question={question} allQuestions={allQuestions} onChange={onChange} />
    </div>
  );
}

// ─── Logic editor ───────────────────────────────────────────────────

function LogicEditor({ question, allQuestions, onChange }) {
  const [open, setOpen] = useState(false);
  const logic = question.logic || {};
  const rules = logic.rules || [];
  const later = allQuestions
    .filter(q => q.id !== question.id)
    .map(q => ({ id: q.id, label: `Q${q.position}: ${q.prompt || "(untitled)"}` }));

  const setLogic = (patch) => onChange({ logic: { ...logic, ...patch } });
  const addRule    = () => setLogic({ rules: [...rules, { match_values: [], then: "" }] });
  const updateRule = (idx, patch) => setLogic({ rules: rules.map((r, i) => i === idx ? { ...r, ...patch } : r) });
  const removeRule = (idx) => setLogic({ rules: rules.filter((_, i) => i !== idx) });
  // Wipe the whole block. Removing rules one by one still left default_next
  // set, so the question kept branching with nothing on screen to show it.
  const clearLogic = () => { onChange({ logic: {} }); setOpen(false); };
  const hasLogic = rules.length > 0 || Boolean(logic.default_next);
  const setNpsBranches = () => setLogic({
    rules: [
      { match_values: [ "0","1","2","3","4","5","6" ], then: "" },
      { match_values: [ "7","8" ], then: "" },
      { match_values: [ "9","10" ], then: "" },
    ],
  });

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="zz-link text-xs self-start">
        {hasLogic ? "edit branching logic" : "+ add branching logic"}
        {hasLogic && (
          <span className="ml-1 text-slate-500">
            ({rules.length > 0 ? `${rules.length} rule${rules.length === 1 ? "" : "s"}` : "default set"})
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="zz-panel p-3 text-sm">
      <div className="flex items-center justify-between mb-2">
        <p className="zz-section blue m-0">logic</p>
        <div className="flex items-center gap-3">
          {hasLogic && (
            <button type="button"
                    onClick={() => { if (confirm("Remove all branching logic from this question?")) clearLogic(); }}
                    className="zz-link text-xs text-ssred">remove branching</button>
          )}
          <button type="button" onClick={() => setOpen(false)} className="zz-link text-xs">collapse</button>
        </div>
      </div>
      <label className="zz-label">Default next</label>
      <select value={logic.default_next || ""} onChange={(e) => setLogic({ default_next: e.target.value || null })} className="zz-input text-sm">
        <option value="">Next question (sequential)</option>
        <option value="end">Skip to the end</option>
        {later.map(q => <option key={q.id} value={q.id}>Jump to {q.label}</option>)}
      </select>

      {question.kind === "nps" && rules.length === 0 && (
        <button type="button" onClick={setNpsBranches} className="zz-btn sm mt-2">
          + set Detractor / Passive / Promoter branches
        </button>
      )}

      {rules.length > 0 && (
        <div className="mt-3 space-y-2">
          {rules.map((r, i) => (
            <div key={i} className="bg-white border-[2px] border-black/30 rounded p-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-600">if answer is</span>
                <button onClick={() => removeRule(i)} className="text-slate-400 hover:text-ssred" title="remove rule"><FiTrash2 /></button>
              </div>
              <ValuesPicker question={question} value={r.match_values || []} onChange={(vals) => updateRule(i, { match_values: vals })} />
              <label className="zz-label mt-2">then</label>
              <select value={r.then || ""} onChange={(e) => updateRule(i, { then: e.target.value })} className="zz-input text-sm">
                <option value="">— pick a destination —</option>
                <option value="end">Skip to the end</option>
                {later.map(q => <option key={q.id} value={q.id}>Jump to {q.label}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}

      <button type="button" onClick={addRule} className="zz-btn sm mt-2">+ add rule</button>
      {later.length === 0 && <p className="text-xs text-slate-500 italic mt-2">Add more questions to enable jump targets.</p>}
      <p className="text-[11px] text-slate-500 italic mt-2">Backward jumps allowed; the editor rejects rules that would form a true loop.</p>
    </div>
  );
}

// ─── Show-if editor ─────────────────────────────────────────────────

function ShowIfEditor({ question, allQuestions, onChange }) {
  const visible_if = question.visible_if || {};
  const hasRule = Boolean(visible_if.question_id);
  const earlier = allQuestions
    .filter(q => q.position < question.position)
    .map(q => ({ id: q.id, label: `Q${q.position}: ${q.prompt || "(untitled)"}` }));
  const dependency = allQuestions.find(q => q.id === visible_if.question_id);

  const setRule = (patch) => onChange({ visible_if: { ...visible_if, ...patch } });
  const clear   = () => onChange({ visible_if: {} });

  if (!hasRule) {
    return (
      <button type="button" onClick={() => earlier.length && setRule({ question_id: earlier[0].id, match_values: [] })}
              disabled={earlier.length === 0}
              className={`zz-link text-xs self-start ${earlier.length === 0 ? "opacity-40 cursor-not-allowed" : ""}`}>
        + only show this question if…
        {earlier.length === 0 && <span className="ml-2 text-[11px] text-slate-500 italic">(add an earlier question first)</span>}
      </button>
    );
  }

  return (
    <div className="zz-panel p-3 text-sm">
      <div className="flex items-center justify-between mb-2">
        <p className="zz-section orange m-0">only show if…</p>
        <button type="button" onClick={clear} className="zz-link text-xs">remove</button>
      </div>
      <label className="zz-label">depends on</label>
      <select value={visible_if.question_id || ""} onChange={(e) => setRule({ question_id: Number(e.target.value), match_values: [] })} className="zz-input text-sm">
        {earlier.map(q => <option key={q.id} value={q.id}>{q.label}</option>)}
      </select>
      <label className="zz-label mt-2">answer matches any of</label>
      {dependency
        ? <ValuesPicker question={dependency} value={visible_if.match_values || []} onChange={(vals) => setRule({ match_values: vals })} />
        : <p className="text-[11px] text-slate-500 italic">(pick a dependency above)</p>}
    </div>
  );
}

function ValuesPicker({ question, value, onChange }) {
  const opts = question.options || [];
  const isChoiceLike = question.kind === "single" || question.kind === "multi" || question.kind === "nps" || question.kind === "numeric";
  if (!isChoiceLike) {
    return (
      <input type="text" defaultValue={value.join(", ")} onBlur={(e) => onChange(e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
             placeholder="comma-separated values to match" className="zz-input text-sm" />
    );
  }
  const toggle = (o) => onChange(value.includes(o) ? value.filter(x => x !== o) : [...value, o]);
  return (
    <div className="flex flex-wrap gap-1">
      {opts.map((o, i) => {
        const on = value.includes(o);
        return (
          <button key={i} type="button" onClick={() => toggle(o)}
                  className={`text-xs font-bold px-2 py-0.5 rounded-full border ${on ? "bg-slate-900 text-white border-black" : "bg-white border-black/30"}`}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

// Options are held locally while you type and only saved on blur.
//
// They used to be controlled straight off the prop, so every keystroke
// fired a PATCH and then overwrote the field with whatever the server
// echoed back. Responses don't return in order, so a slow reply from
// keystroke 3 would land after keystroke 6 and snap the input back —
// worse the higher the latency, which is why production felt broken.
// The prompt and helper-text fields never had this because they use
// defaultValue + onBlur; options now behave the same way.
function OptionsEditor({ value, onChange }) {
  const [opts, setOpts] = useState(value);

  // Commit only when the draft actually differs, so a blur with no edit
  // doesn't cost a request.
  const commit = (next) => {
    setOpts(next);
    if (next.length !== value.length || next.some((o, i) => o !== value[i])) onChange(next);
  };

  const setOpt = (i, v) => { const next = opts.slice(); next[i] = v; setOpts(next); };
  const add    = () => commit([ ...opts, "" ]);
  const remove = (i) => commit(opts.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-1">
      {opts.map((opt, i) => (
        <div key={i} className="flex items-center gap-2">
          <input type="text"
                 value={opt}
                 onChange={(e) => setOpt(i, e.target.value)}
                 onBlur={() => commit(opts)}
                 onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                 placeholder={`Option ${i + 1}`}
                 className="zz-input text-sm flex-1" />
          <button onClick={() => remove(i)} className="text-slate-400 hover:text-ssred" title="remove"><FiTrash2 /></button>
        </div>
      ))}
      <button type="button" onClick={add} className="zz-btn sm">+ add option</button>
    </div>
  );
}

// ─── Toggle row ─────────────────────────────────────────────────────

function Toggle({ label, hint, checked, onChange }) {
  return (
    <label className="flex items-start gap-2 cursor-pointer">
      <input type="checkbox" checked={Boolean(checked)} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 mt-0.5 accent-sspink" />
      <span className="flex-1">
        <span className="block text-sm font-bold text-slate-800">{label}</span>
        {hint && <span className="block text-[11px] text-slate-500">{hint}</span>}
      </span>
    </label>
  );
}

// ─── Fullscreen preview ──────────────────────────────────────────────

// ─── Defaults ────────────────────────────────────────────────────────

function defaultPromptFor(kind) {
  return {
    short:   "Short question?",
    long:    "Tell us more.",
    single:  "Pick one",
    multi:   "Pick all that apply",
    nps:     "How likely are you to recommend us to a friend or colleague?",
    numeric: "Rate from 1 to 10",
    file:    "Upload an image",
  }[kind] || "?";
}
function defaultOptionsFor(kind) {
  if (kind === "single" || kind === "multi") return ["Option 1", "Option 2"];
  if (kind === "nps") return Array.from({ length: 11 }, (_, i) => String(i));
  if (kind === "numeric") return Array.from({ length: 10 }, (_, i) => String(i + 1));
  return [];
}
